import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";
import {
  SportsEsports as GamingIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { updateUserGamingCode } from "../../redux/userSlice";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500",
);

interface GameLink {
  gameCode: string;
  walletAddress: string;
  linkedAt: string;
  gameName?: string;
  isActive?: boolean;
}

const GamingSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const dispatch = useAppDispatch();
  //const { wallet } = useAppSelector((state) => state.wallet); // Assuming wallet state exists
  const { user } = useAppSelector((state) => state.user);
  const [gameCode, setGameCode] = useState("");
  const [isLinked, setIsLinked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [linkedGameInfo, setLinkedGameInfo] = useState<GameLink | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Check if wallet is already linked to a game on component mount
  useEffect(() => {
    checkExistingGameLink();
  }, []);

  const checkExistingGameLink = async () => {
    try {
      const savedGameLink = localStorage.getItem("gameLinkInfo");
      if (savedGameLink) {
        const gameInfo = JSON.parse(savedGameLink);
        setLinkedGameInfo(gameInfo);
        setIsLinked(true);
        setGameCode(gameInfo.gameCode);
      }
    } catch (err) {
      console.error("Error checking game link:", err);
    }
  };

  const validateGameCode = (code: string): boolean => {
    // Example validation rules - customize as needed
    const gameCodeRegex = /^[A-Z0-9]{6,12}$/;
    return gameCodeRegex.test(code.trim().toUpperCase());
  };

  const handleGameCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setGameCode(value);
    if (error) setError("");
  };

  const handleLinkGame = async () => {
    if (!gameCode.trim()) {
      setError("Please enter a game code");
      return;
    }

    const trimmedCode = gameCode.trim().toUpperCase();

    if (!validateGameCode(trimmedCode)) {
      setError(
        "Invalid game code format. Code should be 6-12 alphanumeric characters",
      );
      return;
    }

    if (!user?.walletAddress) {
      setError("Please connect your wallet first");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Simulate API call to verify game code and link wallet
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mock successful linking
      const gameInfo: GameLink = {
        gameCode: trimmedCode,
        walletAddress: user?.walletAddress,
        linkedAt: new Date().toISOString(),
        gameName: `Game ${trimmedCode}`,
        isActive: true,
      };

      dispatch(updateUserGamingCode({ code: trimmedCode })); // Update gaming code in Redux store

      // Save to localStorage (replace with actual API call)
      localStorage.setItem("gameLinkInfo", JSON.stringify(gameInfo));

      setLinkedGameInfo(gameInfo);
      setIsLinked(true);
      setSuccess(`Successfully linked wallet to game code: ${trimmedCode}`);

      // Optional: Dispatch to Redux store
      // dispatch(setGameLink(gameInfo));
    } catch (err) {
      setError(
        "Failed to link game. Please check your game code and try again.",
      );
      console.error("Error linking game:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditGameCode = () => {
    setIsEditing(true);
    setIsLinked(false);
    setError("");
    setSuccess("");
  };

  const handleUnlinkGame = async () => {
    if (
      window.confirm(
        "Are you sure you want to unlink your wallet from this game?",
      )
    ) {
      setLoading(true);
      try {
        // Simulate API call to unlink
        await new Promise((resolve) => setTimeout(resolve, 1000));

        localStorage.removeItem("gameLinkInfo");
        setLinkedGameInfo(null);
        setIsLinked(false);
        setGameCode("");
        setIsEditing(false);
        setSuccess("Wallet successfully unlinked from game");
      } catch (err) {
        setError("Failed to unlink game. Please try again.");
        console.error("Error unlinking game:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCopyGameCode = () => {
    if (linkedGameInfo?.gameCode) {
      navigator.clipboard.writeText(linkedGameInfo.gameCode);
      setSuccess("Game code copied to clipboard!");
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  const scrollableBoxStyles = {
    maxHeight: isMobile ? "50vh" : "60vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  return (
    <Paper
      sx={{
        p: isMobile ? 1 : 2,
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        ...(isMobile && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        ...(!isMobile && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      <Box sx={{ p: isMobile ? 1 : 2 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <GamingIcon
            sx={{
              mr: 1,
              fontSize: isMobile ? 24 : 32,
              color: theme.palette.primary.main,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1.25rem" : "1.5rem",
              fontWeight: "bold",
            }}
          >
            Gaming Section
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {!isLinked || isEditing ? (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Link your wallet to a game using a valid game code
              </Typography>

              <TextField
                fullWidth
                label="Game Code"
                placeholder="Enter game code (e.g., GAME123456)"
                value={gameCode}
                onChange={handleGameCodeChange}
                disabled={loading || (isLinked && !isEditing)}
                error={!!error}
                helperText={
                  error || "Enter the game code provided by the game developer"
                }
                size={isMobile ? "small" : "medium"}
                sx={{ mt: 2, mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GamingIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                autoComplete="off"
              />

              {!user?.walletAddress && (
                <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                  Please connect your wallet before linking to a game
                </Alert>
              )}

              <Box display="flex" gap={2} justifyContent="flex-end">
                {isEditing && (
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      setGameCode(linkedGameInfo?.gameCode || "");
                      setError("");
                    }}
                    disabled={loading}
                    size={isMobile ? "small" : "medium"}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handleLinkGame}
                  disabled={loading || !gameCode.trim() || !user?.walletAddress}
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <LinkIcon />
                  }
                  size={isMobile ? "small" : "medium"}
                >
                  {loading
                    ? "Linking..."
                    : isEditing
                      ? "Update Link"
                      : "Link Game"}
                </Button>
              </Box>

              {success && (
                <Alert
                  severity="success"
                  sx={{ mt: 2 }}
                  onClose={() => setSuccess("")}
                >
                  {success}
                </Alert>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Display linked game information */}
            <Card sx={{ mb: 3, bgcolor: theme.palette.success.light + "10" }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box flex={1}>
                    <Typography
                      variant="subtitle2"
                      color="success.main"
                      gutterBottom
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                      />
                      Wallet Successfully Linked
                    </Typography>

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                    >
                      Game Code:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
                        {linkedGameInfo?.gameCode}
                      </Typography>
                      <IconButton size="small" onClick={handleCopyGameCode}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Typography variant="body2" color="textSecondary">
                      Linked Wallet:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", mb: 1 }}
                    >
                      {linkedGameInfo?.walletAddress}
                    </Typography>

                    <Typography variant="body2" color="textSecondary">
                      Linked At:
                    </Typography>
                    <Typography variant="body2">
                      {linkedGameInfo?.linkedAt &&
                        new Date(linkedGameInfo.linkedAt).toLocaleString()}
                    </Typography>

                    <Chip
                      icon={<LockIcon />}
                      label="Game Link Active"
                      color="success"
                      size="small"
                      sx={{ mt: 2 }}
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <IconButton
                      onClick={handleEditGameCode}
                      size="small"
                      color="primary"
                      title="Edit game code"
                    >
                      <EditIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                    <IconButton
                      onClick={handleUnlinkGame}
                      size="small"
                      color="error"
                      title="Unlink game"
                    >
                      <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mb: 2 }}>
              Your wallet is currently linked to this game. The game code field
              is locked for security. Click the edit button to change the game
              code.
            </Alert>
          </>
        )}

        {/* Game Features Section */}
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
          Game Features
        </Typography>

        <Paper variant="outlined" sx={scrollableBoxStyles}>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="textSecondary" paragraph>
              Once your wallet is linked to a game, you'll be able to:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <Typography variant="body2" paragraph>
                  Earn in-game rewards directly to your wallet
                </Typography>
              </li>
              <li>
                <Typography variant="body2" paragraph>
                  Trade game items and NFTs
                </Typography>
              </li>
              <li>
                <Typography variant="body2" paragraph>
                  Participate in tournaments and events
                </Typography>
              </li>
              <li>
                <Typography variant="body2" paragraph>
                  Track your gaming achievements on-chain
                </Typography>
              </li>
            </ul>
          </Box>
        </Paper>

        {/* Security Note */}
        {isLinked && (
          <Alert severity="warning" sx={{ mt: 2 }} icon={<LockIcon />}>
            <Typography variant="body2">
              This game code is linked to your wallet address. Keep your game
              code secure and never share it with anyone. To change the game
              code, you'll need to unlink the current one first.
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default GamingSection;
